import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { formatLicensePlate } from '@vwpfs/mary-air-lib/utils/LicensePlate';
import { getKeycloak, withVerifiedToken } from '../auth/keycloak';
import { createGraphQLClient } from '../lib/graphql-client';
import { VEHICLES_LIST_QUERY } from '../lib/queries';
import type { UseForAdSetting, VehicleListItem, VehicleSearchFilters, VehiclesListResponse } from '../types/vehicle';

export const PAGE_LIMIT = 10;

type GraphQLItem = Omit<
  VehicleListItem,
  'dealerName' | 'dealerLocationId' | 'mobilistStatusReserved' | 'mobilistUseForAd'
> & {
  dealer?: { name?: string | null; locationId?: number | null } | null;
  mobilistStatusReserved?: boolean | null;
  mobilistUseForAd?: Array<{ broker: string; product: string; useForAd: boolean }> | null;
};

interface VehiclesListGraphQLResponse {
  managementVehiclesList: {
    count: number | null;
    items: GraphQLItem[];
  };
}

async function searchBackend(filters: VehicleSearchFilters, pageNumber: number): Promise<VehiclesListResponse> {
  return withVerifiedToken(getKeycloak(), async (verifiedToken) => {
    const client = createGraphQLClient(verifiedToken);
    const licensePlate = filters.licensePlate?.trim();
    const formattedPlate = licensePlate ? (formatLicensePlate(licensePlate) ?? licensePlate) : undefined;
    const variables = {
      input: {
        ...(filters.id ? { id: filters.id } : {}),
        ...(filters.komNr ? { komNr: filters.komNr } : {}),
        ...(formattedPlate ? { licensePlate: formattedPlate } : {}),
        ...(filters.brand ? { brand: filters.brand } : {}),
        ...(filters.model ? { model: filters.model } : {}),
        ...(filters.dealer ? { dealer: { name: filters.dealer } } : {}),
        limit: PAGE_LIMIT,
        pageNumber
      }
    };
    const data = await client.request<VehiclesListGraphQLResponse>(VEHICLES_LIST_QUERY, variables);
    const items: VehicleListItem[] = data.managementVehiclesList.items.map(
      ({ dealer, mobilistStatusReserved, mobilistUseForAd, ...rest }) => ({
        ...rest,
        dealerName: dealer?.name ?? null,
        dealerLocationId: dealer?.locationId ?? null,
        mobilistStatusReserved: Boolean(mobilistStatusReserved),
        mobilistUseForAd:
          mobilistUseForAd?.map((setting) => ({
            broker: setting.broker,
            product: setting.product as UseForAdSetting['product'],
            useForAd: setting.useForAd
          })) ?? []
      })
    );

    return {
      count: data.managementVehiclesList.count,
      limit: PAGE_LIMIT,
      pageNumber,
      items
    };
  });
}

/**
 * Whether there is anything to search for. Mirrors the backend's "at least one criterion" rule —
 * every filter the input accepts has to be listed here, or searching by it silently does nothing.
 */
function hasFilter(filters: VehicleSearchFilters): boolean {
  return Boolean(
    filters.id?.trim() ||
    filters.komNr?.trim() ||
    filters.licensePlate?.trim() ||
    filters.brand?.trim() ||
    filters.model?.trim() ||
    filters.dealer?.trim()
  );
}

export const VEHICLE_SEARCH_QUERY_KEY = 'vehicleSearch';

export function useVehicleSearch(
  filters: VehicleSearchFilters,
  pageNumber: number
): UseQueryResult<VehiclesListResponse, Error> {
  return useQuery({
    queryKey: [VEHICLE_SEARCH_QUERY_KEY, filters, pageNumber],
    queryFn: () => searchBackend(filters, pageNumber),
    enabled: hasFilter(filters),
    placeholderData: (prev) => prev
  });
}
